import { useState } from "react";
import { useNavigate } from "react-router";
import { longDate as dataLonga } from "@historia/shared/dates";
import { AFTER_ILLUSTRATION_CHANGE, createIllustration } from "@/api/mutations";
import { useAccao } from "@/api/useAccao";
import { checkFiles, uploadFile } from "@/lib/uploadFile";
import { ApiError } from "@/api/client";

type MarcoResumo = { id: string; titulo: string; data: string };

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-3 text-tinta outline-none transition-colors placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";
const rotulo = "flex flex-col gap-1.5 text-left";
const tituloRotulo = "text-sm font-semibold text-tinta-700";

export function FormIlustracao({ marcos }: { marcos: MarcoResumo[] }) {
  const navigate = useNavigate();
  const [, registar] = useAccao(createIllustration, {
    invalidate: AFTER_ILLUSTRATION_CHANGE,
    onSuccess: () => navigate("/conto"),
  });
  const [ficheiro, setFicheiro] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aEnviar, setAEnviar] = useState(false);

  async function enviar(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);

    if (!ficheiro) {
      setErro("Escolhe a imagem da ilustração.");
      return;
    }
    // Limits from @historia/shared, so they can't drift from the API's.
    const { rejected } = checkFiles([ficheiro], "ilustracao");
    if (rejected.length > 0) {
      setErro(
        rejected[0].includes("formato")
          ? "Formato não suportado. Usa JPG, PNG, WEBP ou GIF."
          : "A imagem é demasiado grande (máx. 15 MB).",
      );
      return;
    }

    const dados = new FormData(e.currentTarget);

    setAEnviar(true);
    try {
      // compress -> sign -> PUT to R2 -> confirm (STACK.md §9)
      const { mediaId } = await uploadFile(ficheiro, "ilustracao");

      const resultado = await registar({
        mediaId,
        titulo: String(dados.get("titulo") ?? ""),
        descricao: String(dados.get("descricao") ?? "") || null,
        data: String(dados.get("data") ?? "") || null,
        marcoId: String(dados.get("marco_id") ?? "") || null,
      });

      if (resultado.ok) return; // onSuccess navigates to /conto

      // No orphan cleanup needed any more: a failure here just leaves a
      // `pending` media row, which the nightly sweep collects (STACK.md §9).
      setErro(resultado.erro ?? "Não foi possível guardar.");
      setAEnviar(false);
    } catch (error) {
      setErro(
        error instanceof ApiError ? error.message : "Algo correu mal ao enviar. Tenta novamente.",
      );
      setAEnviar(false);
    }
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      <label className={rotulo}>
        <span className={tituloRotulo}>Imagem da ilustração</span>
        <input
          type="file"
          // image/* abre logo a galeria no telemóvel (uma lista de MIME fixa fazia
          // o iOS abrir os Ficheiros em vez das Fotos). A validação por tipo é feita
          // a seguir no envio, por isso continua seguro.
          accept="image/*"
          required
          onChange={(e) => setFicheiro(e.target.files?.[0] ?? null)}
          className="rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-3 text-sm text-tinta-700 file:mr-3 file:rounded-full file:border-0 file:bg-ouro/15 file:px-4 file:py-1.5 file:text-sm file:font-semibold file:text-ouro-600 hover:file:bg-ouro/25"
        />
        {ficheiro && (
          <span className="text-xs text-tinta-700/70">Selecionada: {ficheiro.name}</span>
        )}
        <span className="text-xs text-tinta-700/60">JPG, PNG, WEBP ou GIF · até 15 MB</span>
      </label>

      <label className={rotulo}>
        <span className={tituloRotulo}>Título do momento</span>
        <input name="titulo" required maxLength={120} className={campo} placeholder="O nosso primeiro Natal" />
      </label>

      <label className={rotulo}>
        <span className={tituloRotulo}>Descrição (ao lado da imagem)</span>
        <textarea
          name="descricao"
          rows={5}
          className={`${campo} resize-none`}
          placeholder="Conta a história deste momento…"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className={rotulo}>
          <span className={tituloRotulo}>Data (opcional)</span>
          <input type="date" name="data" className={campo} />
        </label>
        <label className={rotulo}>
          <span className={tituloRotulo}>Ligar a um marco (opcional)</span>
          <select name="marco_id" defaultValue="" className={campo}>
            <option value="">— Nenhum —</option>
            {marcos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.titulo} ({dataLonga(m.data)})
              </option>
            ))}
          </select>
        </label>
      </div>

      {erro && (
        <p
          role="alert"
          className="rounded-2xl border border-blush/60 bg-blush-300/30 px-4 py-2.5 text-sm text-tinta"
        >
          {erro}
        </p>
      )}

      <button
        type="submit"
        disabled={aEnviar}
        className="mt-2 inline-flex items-center justify-center rounded-full bg-ouro px-7 py-3 text-base font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all duration-300 hover:bg-ouro-600 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ouro/60 focus-visible:ring-offset-2 focus-visible:ring-offset-creme disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {aEnviar ? "A enviar…" : "Guardar ilustração"}
      </button>
    </form>
  );
}
